import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Camera, FileText, FolderInput, FolderOpen, Loader2, Printer, Search, Trash2, Upload, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useLiveRefresh, useLiveEvent } from "@/lib/realtime";
import { useOfflineUploads, enqueue } from "@/lib/offlineUploads";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import BatchScans from "@/components/BatchScans";
import QboBalance from "@/components/QboBalance";
import EmailStatementButton from "@/components/EmailStatementButton";
import ScanSelectBar from "@/components/ScanSelectBar";
import CameraCapture from "@/components/CameraCapture";
import { Checkbox } from "@/components/ui/checkbox";

export async function openFile(f) {
  const w = window.open("", "_blank");
  try {
    const r = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
    w.location.href = URL.createObjectURL(r.data);
  } catch (e) {
    w?.close();
    toast.error(formatError(e));
  }
}

export function Thumb({ file, onOpen }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!file.content_type.startsWith("image/")) return undefined;
    let u;
    api.get(`/files/${file.id}/download`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [file]);
  return (
    <button onClick={() => onOpen(file)} className="aspect-[3/4] w-full rounded-md bg-secondary overflow-hidden flex items-center justify-center" data-testid={`file-thumb-${file.id}`}>
      {url ? <img src={url} alt={file.original_filename} className="w-full h-full object-cover" /> : <FileText className="text-primary" size={32} />}
    </button>
  );
}

function Folder({ customer, onCount }) {
  const { isAdmin } = useAuth();
  const [files, setFiles] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [qboOn, setQboOn] = useState(false);
  useEffect(() => { setSelected(new Set()); }, [customer.key]);
  useEffect(() => { if (isAdmin) api.get("/qbo/status").then((r) => setQboOn(r.data.connected)).catch(() => {}); }, [isAdmin]);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [invoiceNo, setInvoiceNo] = useState("");
  const [busy, setBusy] = useState(false);
  const [camOpen, setCamOpen] = useState(false);
  const pick = useRef(null);
  const base = `/customers/${encodeURIComponent(customer.key)}/files`;

  const load = () => api.get(base).then((r) => { setFiles(r.data); onCount(customer.key, r.data.length); }).catch((e) => toast.error(formatError(e)));
  useEffect(() => { setFiles(null); load(); }, [customer.key]); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveRefresh(load);
  const { pending, online } = useOfflineUploads();
  // Refresh the folder whenever the offline queue changes (e.g. a queued photo just uploaded).
  useEffect(() => { load(); }, [pending]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (list) => {
    if (!list?.length) return;
    setBusy(true);
    let ok = 0, queued = 0;
    for (const f of list) {
      if (!navigator.onLine) {
        await enqueue({ base, customerKey: customer.key, customerName: customer.name, invoice_number: invoiceNo, filename: f.name, blob: f });
        queued += 1;
        continue;
      }
      const form = new FormData();
      form.append("file", f);
      form.append("invoice_number", invoiceNo);
      try {
        await api.post(base, form);
        ok += 1;
      } catch (e) {
        if (!e?.response) {
          await enqueue({ base, customerKey: customer.key, customerName: customer.name, invoice_number: invoiceNo, filename: f.name, blob: f });
          queued += 1;
        } else {
          toast.error(`${f.name}: ${formatError(e)}`);
        }
      }
    }
    if (ok) toast.success(`Saved ${ok} file${ok === 1 ? "" : "s"} to ${customer.name}`);
    if (queued) toast(`${queued} photo${queued === 1 ? "" : "s"} saved offline — will upload automatically when you're back online`);
    setBusy(false);
    setInvoiceNo("");
    load();
  };

  const open = openFile;

  const remove = async (f) => {
    try { await api.delete(`/files/${f.id}`); toast.success("File removed"); load(); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="fade-up" data-testid="customer-folder">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-mono uppercase tracking-widest text-cyan-400">Customer folder</p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-1" data-testid="customer-folder-name">{customer.name}</h2>
          <p className="text-sm text-muted-foreground mt-1">{[customer.city, customer.phone, customer.contact].filter(Boolean).join(" · ")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && <EmailStatementButton customer={customer} />}
          <Button variant="outline" onClick={() => window.open(`/statement?customer=${encodeURIComponent(customer.name)}`, "_blank")} data-testid="print-statement-button" className="hover:bg-secondary hover:text-foreground"><Printer size={16} /> Print statement</Button>
        </div>
      </div>
      {isAdmin && <QboBalance name={customer.name} />}
      <div className="mt-5 border rounded-lg bg-card p-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Invoice # (optional)</p>
          <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder="e.g. C26081801" className="w-48" data-testid="scan-invoice-number" />
        </div>
        <input ref={pick} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} data-testid="scan-file-input" />
        <Button onClick={() => setCamOpen(true)} disabled={busy} data-testid="scan-camera-button" className="hover:bg-blue-600 hover:text-white">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />} Take photo
        </Button>
        <Button variant="outline" onClick={() => pick.current.click()} disabled={busy} data-testid="scan-upload-button"><Upload size={16} /> Upload photo or PDF</Button>
        {(!online || pending > 0) && (
          <span className="flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-500/40 px-3 py-1.5 text-xs font-semibold text-amber-600 dark:text-amber-400" data-testid="offline-queue-indicator">
            <WifiOff size={13} />
            {!online && "Offline"}
            {pending > 0 && `${!online ? " · " : ""}${pending} waiting to upload`}
          </span>
        )}
      </div>
      <CameraCapture open={camOpen} onClose={() => setCamOpen(false)} onCapture={(file) => upload([file])} />
      {isAdmin && <ScanSelectBar files={files} selected={selected} setSelected={setSelected} qboConnected={qboOn} onDone={load} />}
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="customer-files">
        {!files && <Loader2 className="animate-spin text-primary" />}
        {files?.map((f) => (
          <div key={f.id} className={`space-y-1.5 relative rounded-md ${selected.has(f.id) ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""}`} data-testid={`customer-file-${f.id}`}>
            {isAdmin && <div className="absolute top-2 left-2 z-10 rounded bg-background/80 p-1"><Checkbox checked={selected.has(f.id)} onCheckedChange={() => toggle(f.id)} data-testid={`scan-select-${f.id}`} aria-label={`Select ${f.original_filename}`} /></div>}
            {f.in_quickbooks && <span className="absolute top-2 right-2 z-10 rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid={`scan-qbo-badge-${f.id}`}>QB</span>}
            <Thumb file={f} onOpen={open} />
            <div className="flex items-start justify-between gap-2 text-xs">
              <div className="min-w-0">
                <p className="font-mono truncate">{f.invoice_number || f.original_filename}</p>
                <p className="text-muted-foreground">{new Date(f.created_at).toLocaleDateString()} · {f.uploaded_by_name}</p>
              </div>
              {isAdmin && <button onClick={() => remove(f)} className="text-muted-foreground hover:text-red-400" data-testid={`delete-file-${f.id}`} aria-label="Remove file"><Trash2 size={13} /></button>}
            </div>
          </div>
        ))}
        {files?.length === 0 && <p className="col-span-full text-sm text-muted-foreground" data-testid="customer-files-empty">No scans yet. Use "Take photo" to snap one, or "Upload photo or PDF".</p>}
      </div>
    </div>
  );
}

export default function Customers() {
  const [customers, setCustomers] = useState(null);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [deepDone, setDeepDone] = useState(false);

  const loadCustomers = () => api.get("/customers").then((r) => setCustomers(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { loadCustomers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveRefresh(loadCustomers);
  // Deep-link: /customers?key=<customer_key>&name=<name> auto-opens that customer's folder.
  useEffect(() => {
    if (deepDone || !customers) return;
    const key = searchParams.get("key");
    if (!key) return;
    const found = customers.find((c) => c.key === key) || { key, name: searchParams.get("name") || key, city: "", file_count: 0 };
    setActive(found);
    setDeepDone(true);
    setSearchParams({}, { replace: true });
  }, [customers, deepDone, searchParams, setSearchParams]);
  // Flash a badge the instant a tech files a new photo/scan from the field.
  const [flashed, setFlashed] = useState({});
  useLiveEvent((d) => {
    if (d.method !== "POST") return;
    const m = /^\/api\/customers\/(.+)\/files$/.exec(d.path || "");
    if (!m) return;
    let key; try { key = decodeURIComponent(m[1]); } catch { key = m[1]; }
    setFlashed((f) => ({ ...f, [key]: Date.now() }));
    toast.success("New field photo received", { description: key });
    setTimeout(() => setFlashed((f) => { const n = { ...f }; delete n[key]; return n; }), 10000);
  });
  const shown = useMemo(() => (customers || []).filter((c) => `${c.name} ${c.city || ""}`.toLowerCase().includes(q.toLowerCase())), [customers, q]);
  const setCount = (key, n) => setCustomers((cs) => cs.map((c) => (c.key === key ? { ...c, file_count: n } : c)));

  return (
    <div className="grid lg:grid-cols-[320px_minmax(0,1fr)] gap-6">
      <div className={`${active ? "hidden lg:block" : ""}`}>
        <p className="text-xs font-mono uppercase tracking-widest text-cyan-400">From your main database</p>
        <h1 className="text-3xl font-bold tracking-tight mt-1">Customers</h1>
        <div className="relative mt-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers" className="pl-9" data-testid="customer-search-input" />
        </div>
        <Button variant={active === "batch" ? "default" : "outline"} onClick={() => setActive("batch")} className="w-full mt-3 justify-start" data-testid="batch-scan-button"><FolderInput size={16} /> Batch scan folder</Button>
        <div className="mt-3 border rounded-lg bg-card divide-y max-h-[70vh] overflow-y-auto" data-testid="customer-list">
          {!customers && <div className="p-6"><Loader2 className="animate-spin text-primary" /></div>}
          {shown.map((c, i) => (
            <button key={c.key} onClick={() => setActive(c)} data-testid={`customer-item-${i}`}
              className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${active?.key === c.key ? "bg-primary/10" : "hover:bg-secondary/60"}`}>
              <FolderOpen size={16} className="text-primary shrink-0" />
              <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium">{c.name}</span><span className="block truncate text-xs text-muted-foreground">{c.city || "—"}</span></span>
              {flashed[c.key] && (
                <span data-testid={`customer-new-photo-${i}`} className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-semibold px-2 py-0.5 animate-pulse">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> New photo
                </span>
              )}
              <span className="font-mono text-xs text-muted-foreground" data-testid={`customer-file-count-${i}`}>{c.file_count}</span>
            </button>
          ))}
          {customers?.length === 0 && <p className="p-6 text-sm text-muted-foreground" data-testid="customer-empty">No customers found. Set a main database (admin) or ask for access.</p>}
        </div>
      </div>
      <div>
        {active ? (
          <>
            <button className="lg:hidden text-xs text-primary mb-3" onClick={() => setActive(null)} data-testid="customer-back-button">← All customers</button>
            {active === "batch" ? <BatchScans customers={customers || []} onFiled={loadCustomers} /> : <Folder customer={active} onCount={setCount} />}
          </>
        ) : <p className="hidden lg:block text-muted-foreground mt-16">Pick a customer to open their folder.</p>}
      </div>
    </div>
  );
}
